import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Loader } from '../components/common';
import { UserIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export default function Plannings({ connectedUser, token }) {
    const [week, setWeek] = useState('current');


    // Get current date
    const currentDate = new Date();

    // Get the current day of the week (0-6, where 0 is Sunday)
    const currentDayOfWeek = currentDate.getDay();

    // Calculate the difference in days to Monday (considering Sunday as the first day of the week)
    const daysUntilMonday = (currentDayOfWeek + 6) % 7;

    // Set the date to Monday of the current week
    const mondayOfCurrentWeek = new Date(currentDate);
    mondayOfCurrentWeek.setDate(currentDate.getDate() - daysUntilMonday);

    // Set the date to Sunday of the next week
    const sundayOfNextWeek = new Date(mondayOfCurrentWeek);
    sundayOfNextWeek.setDate(mondayOfCurrentWeek.getDate() + 13);

    const [loadingPlanningsData, setLoadingPlanningsData] = useState(false);
    const [plannings, setPlannings] = useState({
        users: {},
        data: {},
    });

    const fetchPlanningsData = async () => {
        setLoadingPlanningsData(true);
        try {
            const url = new URL(`${process.env.REACT_APP_API_URL}/plannings`);

            const payload = {
                start_date: `${mondayOfCurrentWeek.getDate()}/${mondayOfCurrentWeek.getMonth() + 1}/${mondayOfCurrentWeek.getFullYear()}`,
                end_date: `${sundayOfNextWeek.getDate()}/${sundayOfNextWeek.getMonth() + 1}/${sundayOfNextWeek.getFullYear()}`,
            };
            Object.keys(payload).map(key => url.searchParams.append(key, payload[key]));

            const response = await fetch(url.href, {
                method: 'GET',
                headers: {
                    'access-key': token,
                },
            });

            if (response.ok) {
                const data = await response.json();

                // Format planningsData accordingly
                let formattedData = {};
                let users = {};
                data.plannings.map(item => {
                    const user = item.user;

                    if (!user || !user.hasOwnProperty('uid')) return;

                    let d = new Date(item.date);
                    if (!Object.keys(formattedData).includes(user.uid)) {
                        formattedData[user.uid] = [];
                    }

                    if (!Object.keys(users).includes(user.uid)) {
                        users[user.uid] = user;
                    }

                    formattedData[user.uid][d.toLocaleDateString()] = item.status;
                });


                setPlannings({
                    users: users,
                    data: formattedData,
                });
            } else if (response.status === 400) {
                toast.error('Failed loading plannings!');
            } else {
                // Other error occurred
                toast.error('Failed loading plannings!');
            }
        } catch (error) {
            toast.error('An error occurred while loading plannings!');
        } finally {
            setLoadingPlanningsData(false);
        }
    };

    useEffect(() => {
        fetchPlanningsData();
    }, []);



    const orderDays = (userUid) => {

        let data = plannings.data[userUid];

        let monday = new Date(mondayOfCurrentWeek);
        let sunday = new Date(sundayOfNextWeek);

        if (week === 'current') {
            sunday = sunday.setDate(sunday.getDate() - 7);
        } else {
            monday = monday.setDate(monday.getDate() + 7);
        }

        const datesArray = [];


        // Iterate from Monday to Sunday and add each date to the array
        let date = new Date(monday);
        while (date <= sunday) {
            const d = date.toLocaleDateString();
            let status = '--';

            if (Object.keys(data).includes(d)) {
                status = data[d];
            }

            let day = (new Date(date)).toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
            let weekday = (new Date(date)).toLocaleDateString('en-US', { weekday: 'short' });

            datesArray.push({ day, status, weekday, date: d });


            date.setDate(date.getDate() + 1);
        }

        return datesArray;
    }


    const getStatusBgColor = (status, date = null) => {

        const today = (new Date()).toLocaleDateString();
        date = date ? (new Date(date)).toLocaleDateString() : null;
        let cssClass = '';

        switch (status) {
            case 'off':
                cssClass = ' rounded bg-red-200 ';
                break;
            case '--':
                cssClass = ' rounded bg-gray-200 ';
                break;
            default:
                cssClass = ' rounded bg-primary-300 ';
                break;
        }

        return today === date ? `${cssClass} border border-primary-700` : cssClass;
    }


    return (
        <div className="m-auto w-full md:w-2/3 lg:w-2/3 xl:w-2/3 justify-center items-center relative">
            <h1 className="text-primary-700 text-2xl font-bold mb-4 w-full">Plannings</h1>

            <div className=''>
                <button title='Current week' disabled={week === 'current'} onClick={() => setWeek('current')}
                    className={(week !== 'current' ? ' hover:bg-primary-100 hover:border-primary-300 border text-primary-700 ' : ' text-primary-300 ') + 'mr-2 px-2 bg-gray-100 rounded transition-all'}>
                    <ChevronLeftIcon className='h-8 w-8 text-primary' />
                </button>
                <button title='Next week' disabled={week === 'next'} onClick={() => setWeek('next')}
                    className={(week !== 'next' ? ' hover:bg-primary-100 hover:border-primary-300 border text-primary-700 ' : ' text-primary-300 ') + 'mr-2 px-2 bg-gray-100 rounded transition-all'}>
                    <ChevronRightIcon className='h-8 w-8 text-primary' />
                </button>
            </div>

            {/* Plannings container */}
            <div className="mt-4  m-auto">
                {/* Users container */}
                {Object.keys(plannings.data).map(userUid => {
                    const userPlanning = plannings.users[userUid];
                    // Normal employees shouldn't see admins plannings
                    if (connectedUser.role.name !== 'admin' && userPlanning.role.name === 'admin') return '';

                    return (
                        <div className={(connectedUser.uid === userUid ? ' border-l-4 border-primary-500 bg-gray-100 ' : ' ') + `flex m-auto px-2 py-4 transition-all hover:bg-gray-100`} key={userUid} >
                            <div className={(connectedUser.uid === userUid ? ' border-primary-500 ' : ' border-gray-300 ') + `flex items-center m-auto mx-2 transition-all w-48`}>
                                {plannings.users[userUid].photo ? (
                                    <img className='h-14 w-14 rounded-full' title={plannings.users[userUid].email} src={`${process.env.REACT_APP_API_URL}/${plannings.users[userUid].photo}`} />
                                ) : (
                                    <UserIcon className="h-14 w-14 text-primary-500 rounded-full" />
                                )}
                                <p className='mx-2 text-base '>{plannings.users[userUid].name}</p>
                            </div>
                            {
                                orderDays(userUid).map((item, key) => {
                                    return (<div className={(getStatusBgColor(item.status, item.date)) + ' mx-1 w-32 h-auto p-2 rounded-md text-center'} key={key}>
                                        <p className="">{item.day}</p>
                                        <p className="">{item.weekday}</p>
                                        <p className="">{item.status}</p>
                                    </div>
                                    )
                                }
                                )
                            }
                        </div>
                    )
                })}
            </div>

            <ToastContainer />
            {loadingPlanningsData && <Loader height={'auto'} width={'fit-content'} />}
        </div>
    );
}
